import React, { useEffect, useState } from 'react';
import { getAssetUrl, getRawAssetUrl } from '../../Api';
import { Trilha } from '../Trilha';
import * as S from './style';

// Imagem mais de 5x mais larga que alta não é foto de capa: é a tira de
// cabeçalho antiga (o "Header_Copasul.png", 2734x348, um degradê verde com
// ilustração, ~8:1), que quase todas as páginas antigas têm no `imagemHeader`.
// Esticada numa capa de 560px ela vira um recorte ampliado e estranho. Arte
// larga mas ainda usável (o céu da Irrigação, 4096x1007, ~4:1) passa.
const PROPORCAO_MAXIMA = 5;

// Capa padrão das páginas internas. Cada página só passa a foto e os textos:
//
//   <Capa imagem={[idDoHeader, idDoFundo]} titulo="Fecularia" texto="..." selo="Desde 1978">
//     {botões opcionais}
//   </Capa>
//
// `imagem` é o id de um asset do Squidex, ou uma lista de ids em ordem de
// preferência: a Capa usa o primeiro que existir e for foto (pula vazio, erro e
// a tira antiga).
//
// Sem nenhuma foto, não há capa: fica só o breadcrumb no fundo da página, e o
// conteúdo logo abaixo (sem bloco verde e sem título). Enquanto a foto é
// testada, também só o breadcrumb: a capa aparece quando há o que mostrar.
//
// O breadcrumb vem sozinho do menu; `rota` e `atual` são do <Trilha> (página
// filha que não está no menu, como uma notícia: rota="/noticias" atual={titulo}).
//
// Ela substitui a faixa verde com o título que o Header desenhava: a página
// que usa a Capa chama setTitle(null).

// Testa as candidatas em ordem, fora da tela, e devolve a URL da primeira que
// carrega e tem proporção de foto. null = nenhuma serve; undefined = testando.
const useFoto = (candidatas) => {
  const chave = candidatas.join(',');
  const [foto, setFoto] = useState(candidatas.length ? undefined : null);

  useEffect(() => {
    let vivo = true;
    const ids = chave ? chave.split(',') : [];
    setFoto(ids.length ? undefined : null);

    const carregar = (url) =>
      new Promise((ok) => {
        const img = new Image();
        img.onload = () => ok(img.naturalHeight && img.naturalWidth / img.naturalHeight <= PROPORCAO_MAXIMA ? url : null);
        img.onerror = () => ok(false);
        img.src = url;
      });

    (async () => {
      for (const id of ids) {
        // Se o redimensionado falhar, tenta o original; tira antiga não serve
        // em nenhum dos dois, então nem tenta o original.
        let url = await carregar(getAssetUrl(id, { width: 1920, format: 'webp' }));
        if (url === false) url = await carregar(getRawAssetUrl(id));
        if (url) {
          if (vivo) setFoto(url);
          return;
        }
      }
      if (vivo) setFoto(null);
    })();

    return () => {
      vivo = false;
    };
  }, [chave]);

  return foto;
};

export const Capa = ({ imagem, titulo, texto, selo, rota, atual, children }) => {
  const candidatas = (Array.isArray(imagem) ? imagem : [imagem]).filter(Boolean);
  const foto = useFoto(candidatas);

  if (!foto) {
    return (
      <S.SoTrilha>
        <Trilha rota={rota} atual={atual} />
      </S.SoTrilha>
    );
  }

  return (
    <S.Capa>
      <img src={foto} alt="" decoding="async" />
      <S.Topo>
        <Trilha tom="escuro" rota={rota} atual={atual} />
      </S.Topo>
      <S.Conteudo>
        {selo && <S.Selo>{selo}</S.Selo>}
        {titulo && <h1>{titulo}</h1>}
        {texto && <p>{texto}</p>}
        {React.Children.count(children) > 0 && <S.Acoes>{children}</S.Acoes>}
      </S.Conteudo>
    </S.Capa>
  );
};
